// Selected group/category — shared between the topbar picker (AppShell) and
// the home dashboard. Resolution: URL ?g → last choice (localStorage, same
// key name as the mobile app's pinned default) → first group. Selecting from
// anywhere navigates home with the group in the URL, so the dashboard stays
// bookmarkable — except on the Billing page, which keeps a home that has
// bills.

import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CategoryOrGroup } from './api';
import { hasBilling } from './billing';
import { groupDisplayName } from './groupName';
import { wordingFor } from './projectType';
import { useUser } from './user';

export const GROUP_KEY = 'defaultGroupId'; // same name the mobile app uses

// The app suffixes group ids with their type char ('<id>g' / '<id>c') so
// groups and categories can't collide in one keyspace.
export const groupKey = (g: CategoryOrGroup): string => `${g.id}${g.type}`;

// The name an entry shows (groupDisplayName). In a Sub-metering project every
// apartment shows its building, and a building called only "12" reads
// "Building 12".
export function useGroupName(): (g: CategoryOrGroup) => string {
  const { t } = useTranslation();
  return useCallback(
    (g: CategoryOrGroup) =>
      groupDisplayName(
        g,
        wordingFor(g.project_type) === 'housing'
          ? { building: (name: string) => t('groups.buildingName', { name }) }
          : undefined,
      ),
    [t],
  );
}

export function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function store(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* best effort */
  }
}

export function useSelectedGroup(): {
  groups: CategoryOrGroup[];
  group: CategoryOrGroup | null;
  selectGroup: (g: CategoryOrGroup) => void;
} {
  const { me } = useUser();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const groups = me?.categories_and_groups ?? [];
  const urlKey = params.get('g');
  const group =
    groups.find((g) => groupKey(g) === urlKey) ??
    groups.find((g) => groupKey(g) === readStored(GROUP_KEY)) ??
    groups[0] ??
    null;

  const selectGroup = (g: CategoryOrGroup) => {
    store(GROUP_KEY, groupKey(g));
    const page = pathname === '/billing' && hasBilling([g]) ? '/billing' : '/';
    navigate(`${page}?g=${encodeURIComponent(groupKey(g))}`);
  };

  return { groups, group, selectGroup };
}
